// admin/src/pages/wallet/Wallet.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import api from "../../services/api";
import { formatCurrency } from "../../utils/format";
import { Colors } from "../../constants/Colors";

// Wallet card gradient (brand blue -> deeper blues)
const WALLET_START = Colors.SECONDARY_BLUE; // #0e6bf8
const WALLET_MID = "#0A52C4";
const WALLET_END = "#083A8C";
const ACCENT = Colors.PRIMARY; // orange
const WARN_TEXT = "#b88a00"; // readable version of WARNING on light bg

const T = {
  radius: 16,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

/* ---------------------------------------------------------------- */
/* Data shape returned by GET /admin/wallet/summary                  */
/* ---------------------------------------------------------------- */
const EMPTY_CF = {
  money_in: 0,
  money_in_paymongo: 0,
  money_in_cash: 0,
  money_out: 0,
  money_out_payouts: 0,
  money_out_refunds: 0,
  refund_count: 0,
  net_position: 0,
  operator_obligations: 0,
  cash_commission_owed: 0,
  pending_payouts: 0,
  commission_lifetime: 0,
  commission_paymongo: 0,
  commission_cash: 0,
  commission_month: 0,
  commission_week: 0,
  commission_today: 0,
  commission_refunded: 0,
  today_in: 0,
  today_out: 0,
  month_in: 0,
  month_out: 0,
  cancellation_fees_total: 0,
  cancellation_fees_paymongo: 0,
  cancellation_fees_cash: 0,
  cash_commission_collected: 0,
  platform_share_in_wallet: null,
  expected_platform_share: null,
};

const num = (v) => Number(v) || 0;

const TYPE_META = {
  rental_payment: {
    icon: "bi-arrow-down-left",
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1a`,
  },
  cancellation_fee: {
    icon: "bi-exclamation-circle",
    color: Colors.INFO,
    bg: `${Colors.INFO}1a`,
  },
  refund: {
    icon: "bi-arrow-counterclockwise",
    color: Colors.RED,
    bg: `${Colors.RED}1a`,
  },
  payout: {
    icon: "bi-arrow-up-right",
    color: WARN_TEXT,
    bg: `${Colors.WARNING}26`,
  },
};

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "month", label: "This month" },
  { key: "all", label: "All time" },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "in", label: "Incoming" },
  { key: "out", label: "Outgoing" },
];

/* ---------------------------------------------------------------- */
/* Formatting helpers                                                */
/* ---------------------------------------------------------------- */
const formatDate = (iso) => {
  if (!iso) return "N/A";
  try {
    return new Date(iso).toLocaleDateString("en-PH", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
};

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-PH", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const dayLabel = (iso) => {
  if (!iso) return "Undated";
  const d = new Date(iso);
  if (isNaN(d)) return "Undated";
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return formatDate(iso);
};

// Splits "₱1,234.50" into main part and cents so cents can be styled smaller
const splitAmount = (value) => {
  const str = String(formatCurrency(value));
  const i = str.lastIndexOf(".");
  if (i === -1) return { main: str, cents: "" };
  return { main: str.slice(0, i), cents: str.slice(i) };
};

const groupByDay = (list, getIso) => {
  const out = [];
  list.forEach((item) => {
    const label = dayLabel(getIso(item));
    const last = out[out.length - 1];
    if (last && last.label === label) last.items.push(item);
    else out.push({ label, items: [item] });
  });
  return out;
};

/* ---------------------------------------------------------------- */
/* Styles                                                            */
/* ---------------------------------------------------------------- */
const Styles = () => (
  <style>{`
    .wl-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .wl-page .container-fluid { max-width: 1280px; }

    /* Page banner (same look as Bookings Management) */
    .wl-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: 14px; padding: 26px 30px; margin-bottom: 22px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
    .wl-header::after { content: ""; position: absolute; top: -70px; right: -50px; width: 260px; height: 260px; border-radius: 50%; background: radial-gradient(circle, rgba(255,255,255,.28), rgba(255,255,255,0) 70%); pointer-events: none; }
    .wl-header > * { position: relative; z-index: 1; }
    .wl-header h2 { font-weight: 700; margin: 0; font-size: 22px; }
    .wl-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }
    .wl-header-actions { display: flex; gap: 10px; flex-wrap: wrap; }
    .wl-glass-btn { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,.18); border: 1px solid rgba(255,255,255,.35); border-radius: ${T.radiusSm}px; padding: 8px 14px; font-size: 13px; font-weight: 600; color: #fff; cursor: pointer; text-decoration: none; transition: background .15s; }
    .wl-glass-btn:hover:not(:disabled) { background: rgba(255,255,255,.28); color: #fff; }
    .wl-glass-btn:disabled { opacity: .7; cursor: default; }
    .wl-glass-btn:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
    .wl-spin { animation: wl-rotate .9s linear infinite; }
    @keyframes wl-rotate { to { transform: rotate(360deg); } }

    .wl-rail { display: flex; flex-direction: column; gap: 20px; }

    /* Wallet card */
    .wl-balance { position: relative; overflow: hidden; border-radius: 24px; padding: 26px 28px; color: #fff; min-height: 230px; display: flex; flex-direction: column; justify-content: space-between; gap: 18px;
      background: linear-gradient(135deg, ${WALLET_START} 0%, ${WALLET_MID} 52%, ${WALLET_END} 100%);
      box-shadow: 0 18px 40px rgba(8,58,140,.30), inset 0 1px 0 rgba(255,255,255,.16); }
    .wl-balance::before { content: ""; position: absolute; top: -80px; right: -60px; width: 260px; height: 260px; border-radius: 50%; background: radial-gradient(circle at 30% 30%, rgba(2,200,255,.35), rgba(2,200,255,0) 70%); }
    .wl-balance::after { content: ""; position: absolute; bottom: -120px; left: -70px; width: 280px; height: 280px; border-radius: 50%; border: 1px solid rgba(255,255,255,.10); box-shadow: 0 0 0 34px rgba(255,255,255,.03), 0 0 0 68px rgba(255,255,255,.02); }
    .wl-balance > * { position: relative; z-index: 1; }
    .wl-balance-top { display: flex; align-items: center; justify-content: space-between; }
    .wl-balance-label { font-size: 14px; font-weight: 500; opacity: .88; }
    .wl-balance-badge { width: 38px; height: 38px; border-radius: 12px; display: grid; place-items: center; font-size: 18px; background: rgba(255,255,255,.14); }
    .wl-balance-value { font-size: 40px; font-weight: 800; letter-spacing: -0.03em; line-height: 1.05; display: flex; align-items: baseline; font-variant-numeric: tabular-nums; }
    .wl-balance-cents { font-size: 20px; font-weight: 600; opacity: .75; letter-spacing: 0; }
    .wl-balance-note { font-size: 12px; opacity: .75; margin-top: 8px; line-height: 1.4; }
    .wl-balance-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 12.5px; opacity: .9; flex-wrap: wrap; }
    .wl-balance-foot .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 8px; }

    /* Cards */
    .wl-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .wl-card-pad { padding: 20px 22px; }
    .wl-card-title { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
    .wl-card-title h5 { margin: 0; font-size: 14.5px; font-weight: 600; color: ${Colors.TEXT_DARK}; }
    .wl-card-title small { display: block; font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin-top: 1px; }
    .wl-ico { width: 40px; height: 40px; border-radius: 12px; display: grid; place-items: center; font-size: 18px; flex-shrink: 0; }

    /* Commission card */
    .wl-com-value { font-size: 34px; font-weight: 800; letter-spacing: -0.02em; color: ${Colors.TEXT_DARK}; font-variant-numeric: tabular-nums; line-height: 1.1; }
    .wl-com-sub { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; margin: 4px 0 0; }
    .wl-com-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 16px; }
    .wl-mini { background: ${Colors.LIGHT_GRAY}; border-radius: 12px; padding: 10px 12px; }
    .wl-mini-label { font-size: 11.5px; color: ${Colors.TEXT_LIGHT}; margin: 0 0 2px; }
    .wl-mini-value { font-size: 14.5px; font-weight: 700; color: ${Colors.TEXT_DARK}; margin: 0; font-variant-numeric: tabular-nums; }
    .wl-split { display: flex; height: 8px; border-radius: 999px; overflow: hidden; background: ${Colors.LIGHT_GRAY}; margin-top: 18px; }
    .wl-split > span { display: block; height: 100%; }
    .wl-legend { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin-top: 8px; }
    .wl-legend b { color: ${Colors.TEXT_DARK}; font-weight: 600; }
    .wl-legend i { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; }
    .wl-com-foot { margin-top: 14px; padding-top: 12px; border-top: 1px dashed ${Colors.BORDER_GRAY}; display: flex; flex-direction: column; gap: 6px; font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }
    .wl-com-foot div { display: flex; justify-content: space-between; gap: 12px; }
    .wl-com-foot b { color: ${Colors.TEXT_DARK}; font-weight: 600; font-variant-numeric: tabular-nums; }

    /* Obligation rows */
    .wl-side-head { padding: 16px 20px 6px; font-size: 14px; font-weight: 600; color: ${Colors.TEXT_DARK}; }
    .wl-line { display: flex; align-items: center; gap: 14px; padding: 14px 20px; }
    .wl-line + .wl-line { border-top: 1px solid ${Colors.BORDER_GRAY}; }
    .wl-line-label { font-size: 13.5px; font-weight: 600; color: ${Colors.TEXT_DARK}; margin: 0; }
    .wl-line-hint { font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin: 2px 0 0; line-height: 1.35; }
    .wl-line-value { margin-left: auto; padding-left: 8px; font-size: 16px; font-weight: 700; color: ${Colors.TEXT_DARK}; font-variant-numeric: tabular-nums; white-space: nowrap; }

    /* Segmented control + tabs */
    .wl-seg { display: inline-flex; background: ${Colors.LIGHT_GRAY}; border-radius: 999px; padding: 3px; gap: 2px; }
    .wl-seg button { border: 0; background: transparent; border-radius: 999px; padding: 6px 14px; font-size: 12.5px; font-weight: 600; color: ${Colors.TEXT_LIGHT}; cursor: pointer; transition: background .15s, color .15s; }
    .wl-seg button:hover { color: ${Colors.TEXT_DARK}; }
    .wl-seg button.active { background: #fff; color: ${WALLET_START}; box-shadow: 0 1px 3px rgba(16,24,40,.12); }
    .wl-seg button:focus-visible, .wl-tab:focus-visible { outline: 2px solid ${WALLET_START}; outline-offset: 2px; }
    .wl-tabs { display: flex; gap: 22px; border-bottom: ${T.border}; }
    .wl-tab { background: none; border: 0; padding: 10px 2px; margin-bottom: -1px; font-size: 13.5px; font-weight: 600; color: ${Colors.TEXT_LIGHT}; cursor: pointer; border-bottom: 2px solid transparent; transition: color .15s, border-color .15s; }
    .wl-tab:hover { color: ${Colors.TEXT_DARK}; }
    .wl-tab.active { color: ${Colors.TEXT_DARK}; border-bottom-color: ${ACCENT}; }
    .wl-tab-count { margin-left: 6px; font-size: 11.5px; font-weight: 600; color: ${Colors.TEXT_LIGHT}; background: ${Colors.LIGHT_GRAY}; border-radius: 999px; padding: 1px 7px; }

    /* Cash flow */
    .wl-flow-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
    .wl-flow-head h5 { margin: 0; font-size: 16px; font-weight: 600; color: ${Colors.TEXT_DARK}; }
    .wl-flow-head small { color: ${Colors.TEXT_LIGHT}; font-size: 12.5px; }
    .wl-tiles { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .wl-tile { border: ${T.border}; border-radius: 14px; padding: 14px 16px; }
    .wl-tile-label { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; margin: 0; }
    .wl-tile-label i { width: 22px; height: 22px; border-radius: 7px; display: grid; place-items: center; font-size: 12px; }
    .wl-tile-value { font-size: 24px; font-weight: 700; margin: 8px 0 2px; font-variant-numeric: tabular-nums; color: ${Colors.TEXT_DARK}; }
    .wl-tile-hint { font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin: 0; line-height: 1.4; }
    .wl-chart { margin-top: 18px; }
    .wl-chart-legend { display: flex; gap: 16px; font-size: 12px; color: ${Colors.TEXT_LIGHT}; margin-bottom: 6px; }
    .wl-chart-legend i { display: inline-block; width: 9px; height: 9px; border-radius: 3px; margin-right: 6px; }

    /* Activity */
    .wl-act-head { padding: 20px 24px 0; }
    .wl-act-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
    .wl-act-top h5 { margin: 0; font-weight: 600; font-size: 16px; color: ${Colors.TEXT_DARK}; }
    .wl-act-top small { color: ${Colors.TEXT_LIGHT}; font-size: 12.5px; }
    .wl-day { padding: 10px 24px; font-size: 12.5px; font-weight: 600; color: ${Colors.TEXT_LIGHT}; background: ${Colors.LIGHT_GRAY}99; border-bottom: 1px solid ${Colors.BORDER_GRAY}; display: flex; justify-content: space-between; }
    .wl-tx { display: flex; align-items: center; gap: 16px; padding: 14px 24px; border-bottom: 1px solid ${Colors.BORDER_GRAY}; transition: background .15s; }
    .wl-tx:last-child { border-bottom: none; }
    .wl-tx:hover { background: ${Colors.LIGHT_GRAY}80; }
    .wl-tx-icon { width: 42px; height: 42px; border-radius: 50%; display: grid; place-items: center; font-size: 18px; flex-shrink: 0; }
    .wl-tx-main { flex: 1; min-width: 0; }
    .wl-tx-title { font-size: 14px; font-weight: 600; color: ${Colors.TEXT_DARK}; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .wl-tx-sub { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; margin: 2px 0 0; }
    .wl-pill { font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 999px; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .wl-pill.cash { background: ${Colors.SUCCESS}1a; color: ${Colors.SUCCESS}; }
    .wl-tx-right { text-align: right; min-width: 110px; flex-shrink: 0; }
    .wl-tx-amount { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }
    .wl-tx-fee { font-size: 11.5px; color: ${Colors.TEXT_LIGHT}; margin-top: 2px; }
    .wl-note { padding: 12px 24px; font-size: 12px; color: ${Colors.TEXT_LIGHT}; background: ${Colors.LIGHT_GRAY}80; border-top: ${T.border}; }

    .wl-empty { text-align: center; padding: 56px 20px; color: ${Colors.TEXT_LIGHT}; }
    .wl-empty i { font-size: 34px; display: block; margin-bottom: 10px; opacity: .5; }

    .wl-skel { background: linear-gradient(90deg, rgba(150,150,150,.14) 25%, rgba(150,150,150,.26) 50%, rgba(150,150,150,.14) 75%); background-size: 200% 100%; animation: wl-shimmer 1.4s infinite; border-radius: 8px; }
    .wl-skel.on-dark { background: linear-gradient(90deg, rgba(255,255,255,.12) 25%, rgba(255,255,255,.26) 50%, rgba(255,255,255,.12) 75%); background-size: 200% 100%; }
    @keyframes wl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    @media (max-width: 575.98px) {
      .wl-balance { padding: 22px; border-radius: 20px; }
      .wl-balance-value { font-size: 34px; }
      .wl-com-value { font-size: 28px; }
      .wl-com-grid { grid-template-columns: 1fr; }
      .wl-tiles { grid-template-columns: 1fr; }
      .wl-act-head, .wl-day, .wl-tx, .wl-note { padding-left: 16px; padding-right: 16px; }
      .wl-tx { gap: 12px; }
      .wl-pill { display: none; }
      .wl-tx-right { min-width: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .wl-skel, .wl-spin { animation: none; }
    }
  `}</style>
);

/* ---------------------------------------------------------------- */
/* Small components                                                  */
/* ---------------------------------------------------------------- */
const Skeleton = ({ height, width = "100%", dark = false }) => (
  <div
    className={`wl-skel${dark ? " on-dark" : ""}`}
    style={{ height, width }}
  />
);

// Last-14-days bars: money received (blue) vs money paid out (orange)
const FlowChart = ({ data }) => {
  const W = 560;
  const H = 150;
  const padB = 22;
  const padT = 8;
  const max = Math.max(1, ...data.map((d) => Math.max(num(d.in), num(d.out))));
  const slot = W / data.length;
  const bw = Math.min(12, slot * 0.34);
  const h = (v) => ((H - padB - padT) * num(v)) / max;
  const barH = (v) => (num(v) > 0 ? Math.max(h(v), 2) : 0);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label="Money received and paid out per day for the last 14 days"
      style={{ display: "block" }}
    >
      <line
        x1="0"
        x2={W}
        y1={H - padB}
        y2={H - padB}
        stroke={Colors.BORDER_GRAY}
      />
      {data.map((d, i) => {
        const cx = slot * i + slot / 2;
        const showLabel = (data.length - 1 - i) % 3 === 0;
        return (
          <g key={d.date || i}>
            <title>{`${d.label}: received ${formatCurrency(d.in)}, paid out ${formatCurrency(d.out)}`}</title>
            <rect
              x={cx - bw - 1}
              y={H - padB - barH(d.in)}
              width={bw}
              height={barH(d.in)}
              rx="2"
              fill={WALLET_START}
            />
            <rect
              x={cx + 1}
              y={H - padB - barH(d.out)}
              width={bw}
              height={barH(d.out)}
              rx="2"
              fill={ACCENT}
            />
            {showLabel && (
              <text
                x={cx}
                y={H - 6}
                textAnchor="middle"
                fontSize="11"
                fill={Colors.TEXT_LIGHT}
              >
                {d.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

/* ---------------------------------------------------------------- */
/* Page                                                              */
/* ---------------------------------------------------------------- */
const Wallet = () => {
  const [loading, setLoading] = useState(true);
  const [cf, setCf] = useState(EMPTY_CF);
  const [paymongo, setPaymongo] = useState(null);
  const [dailyFlow, setDailyFlow] = useState([]);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState(null);

  const [period, setPeriod] = useState("month");
  const [source, setSource] = useState("ledger"); // ledger | paymongo
  const [filter, setFilter] = useState("all");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Single source of truth: the backend already does all the math
      const res = await api.get("/admin/wallet/summary");
      if (res.data?.success) {
        const d = res.data.data || {};
        setCf({ ...EMPTY_CF, ...(d.cashflow || {}) });
        setPaymongo(d.paymongo || null);
        setDailyFlow(Array.isArray(d.daily_flow) ? d.daily_flow : []);
        setActivity(Array.isArray(d.recent_activity) ? d.recent_activity : []);
      } else {
        setError(res.data?.message || "Failed to load wallet data.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load wallet data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ---------- derived ---------- */
  const balance = splitAmount(cf.net_position);

  // Integrity check: what the wallet holds for the platform vs what it should hold
  const hasRecon =
    cf.platform_share_in_wallet != null && cf.expected_platform_share != null;
  const reconDiff = hasRecon
    ? num(cf.platform_share_in_wallet) - num(cf.expected_platform_share)
    : 0;
  const reconOk = Math.abs(reconDiff) < 0.01;

  const flow = useMemo(() => {
    if (period === "today") return { in: cf.today_in, out: cf.today_out };
    if (period === "month") return { in: cf.month_in, out: cf.month_out };
    return { in: cf.money_in, out: cf.money_out };
  }, [period, cf]);

  // UPDATE: Added cancellation fees to the hint so the math is obvious
  const inHint =
    period === "all"
      ? `Online ${formatCurrency(cf.money_in_paymongo)} · Cash ${formatCurrency(cf.money_in_cash)} · Fees ${formatCurrency(cf.cancellation_fees_total)}`
      : "Rental payments, online and cash";

  const outHint =
    period === "all"
      ? `Payouts ${formatCurrency(cf.money_out_payouts)} · Refunds ${formatCurrency(cf.money_out_refunds)} (${cf.refund_count})`
      : "Operator payouts and refunds";

  const commissionTotal = num(cf.commission_lifetime);
  const onlinePct =
    commissionTotal > 0
      ? Math.min(100, (num(cf.commission_paymongo) / commissionTotal) * 100)
      : 0;
  const cashPct = commissionTotal > 0 ? 100 - onlinePct : 0;

  // Both sources share the same in/out filter
  const rows = useMemo(() => {
    if (source === "paymongo") {
      return (paymongo?.recent_transactions || []).map((t, i) => ({
        key: t.id || `pm-${i}`,
        dir: t.direction,
        title: t.description || "Wallet transaction",
        at: t.created_at,
        amount: t.amount,
        fee: t.fee,
        status: t.status,
      }));
    }
    return activity.map((a) => ({
      key: `${a.type}-${a.id}`,
      dir: a.kind,
      type: a.type,
      title: a.description,
      at: a.at,
      amount: a.amount,
      method: a.method,
      commission: a.commission,
      gross: a.gross,
      withheld: a.withheld,
    }));
  }, [source, activity, paymongo]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      in: rows.filter((r) => r.dir === "in").length,
      out: rows.filter((r) => r.dir === "out").length,
    }),
    [rows],
  );

  const groups = useMemo(() => {
    const list = filter === "all" ? rows : rows.filter((r) => r.dir === filter);
    return groupByDay(list, (r) => r.at);
  }, [rows, filter]);

  const mode = paymongo?.mode;
  const acct = paymongo?.account_number
    ? `••${String(paymongo.account_number).slice(-4)}`
    : null;

  /* ---------- render ---------- */
  return (
    <div className="wl-page">
      <Styles />
      <Container fluid>
        {/* Header banner */}
        <div className="wl-header">
          <div>
            <h2>Wallet & Cash Flow</h2>
            <p>
              Overview of your platform balance, pending payouts, and recent
              activity
            </p>
          </div>
          <div className="wl-header-actions">
            {paymongo?.dashboard_url && (
              <a
                className="wl-glass-btn"
                href={paymongo.dashboard_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-box-arrow-up-right" />
                PayMongo
              </a>
            )}
            <button
              type="button"
              className="wl-glass-btn"
              onClick={loadData}
              disabled={loading}
            >
              <i
                className={`bi bi-arrow-clockwise${loading ? " wl-spin" : ""}`}
              />
              {loading ? "Refreshing" : "Refresh"}
            </button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}
        {!error && paymongo?.error && (
          <Alert variant="warning" className="mb-4">
            PayMongo sync issue: {paymongo.error}. The figures below are
            calculated from your own records and are still shown.
          </Alert>
        )}

        <Row className="g-4">
          {/* ============ Left rail ============ */}
          <Col xs={12} lg={4}>
            <div className="wl-rail">
              {/* Wallet card */}
              <div className="wl-balance">
                <div>
                  <div className="wl-balance-top">
                    <div className="wl-balance-label">Net wallet position</div>
                    <div className="wl-balance-badge">
                      <i className="bi bi-wallet2" />
                    </div>
                  </div>
                  <div className="wl-balance-value" style={{ marginTop: 16 }}>
                    {loading ? (
                      <Skeleton height={42} width="70%" dark />
                    ) : (
                      <>
                        <span>{balance.main}</span>
                        {balance.cents && (
                          <span className="wl-balance-cents">
                            {balance.cents}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="wl-balance-note">
                    Online payments and cancellation fees, minus payouts and
                    refunds. Cash never enters this wallet.
                  </div>
                </div>

                <div className="wl-balance-foot">
                  <span>
                    <span
                      className="dot"
                      style={{
                        background:
                          mode === "test" ? Colors.WARNING : Colors.LIGHT_BLUE,
                        boxShadow: `0 0 0 4px ${
                          mode === "test"
                            ? "rgba(255,193,7,.25)"
                            : "rgba(2,200,255,.22)"
                        }`,
                      }}
                    />
                    {mode === "test" ? "Test mode" : mode ? "Live" : "PayMongo"}
                  </span>
                  <span>
                    {paymongo?.name || "Arkila"}
                    {acct ? ` · ${acct}` : ""}
                  </span>
                </div>
              </div>

              {/* Total commission earned */}
              <div className="wl-card wl-card-pad">
                <div className="wl-card-title">
                  <div
                    className="wl-ico"
                    style={{ background: `${ACCENT}1f`, color: ACCENT }}
                  >
                    <i className="bi bi-graph-up-arrow" />
                  </div>
                  <div>
                    <h5>Total commission earned</h5>
                    <small>Platform share, after refunds</small>
                  </div>
                </div>

                <div className="wl-com-value">
                  {loading ? (
                    <Skeleton height={36} width="60%" />
                  ) : (
                    formatCurrency(cf.commission_lifetime)
                  )}
                </div>
                <p className="wl-com-sub">All time, from rental payments</p>

                <div className="wl-com-grid">
                  {[
                    ["Today", cf.commission_today],
                    ["This week", cf.commission_week],
                    ["This month", cf.commission_month],
                  ].map(([label, val]) => (
                    <div className="wl-mini" key={label}>
                      <p className="wl-mini-label">{label}</p>
                      <p className="wl-mini-value">
                        {loading ? (
                          <Skeleton height={18} width="70%" />
                        ) : (
                          formatCurrency(val)
                        )}
                      </p>
                    </div>
                  ))}
                </div>

                <div
                  className="wl-split"
                  role="img"
                  aria-label={`Commission split: ${onlinePct.toFixed(0)}% online, ${cashPct.toFixed(0)}% cash`}
                >
                  <span
                    style={{
                      width: `${onlinePct}%`,
                      background: WALLET_START,
                    }}
                  />
                  <span style={{ width: `${cashPct}%`, background: ACCENT }} />
                </div>
                <div className="wl-legend">
                  <span>
                    <i style={{ background: WALLET_START }} />
                    Online <b>{formatCurrency(cf.commission_paymongo)}</b>
                  </span>
                  <span>
                    <i style={{ background: ACCENT }} />
                    Cash <b>{formatCurrency(cf.commission_cash)}</b>
                  </span>
                </div>

                <div className="wl-com-foot">
                  <div>
                    <span>Cash commission collected at payout</span>
                    <b>{formatCurrency(cf.cash_commission_collected)}</b>
                  </div>
                  <div>
                    <span>Cancellation fees (separate income)</span>
                    <b>{formatCurrency(cf.cancellation_fees_total)}</b>
                  </div>
                  <div>
                    <span>Commission given up on refunds</span>
                    <b>{formatCurrency(cf.commission_refunded)}</b>
                  </div>
                </div>
              </div>

              {/* Obligations */}
              <div className="wl-card">
                <div className="wl-side-head">Payouts and obligations</div>

                <div className="wl-line">
                  <div
                    className="wl-ico"
                    style={{
                      background: `${Colors.WARNING}26`,
                      color: WARN_TEXT,
                    }}
                  >
                    <i className="bi bi-hourglass-split" />
                  </div>
                  <div>
                    <p className="wl-line-label">Pending payouts</p>
                    <p className="wl-line-hint">
                      Net amount in transit to operators
                    </p>
                  </div>
                  <div className="wl-line-value">
                    {loading ? (
                      <Skeleton height={20} width={80} />
                    ) : (
                      formatCurrency(cf.pending_payouts)
                    )}
                  </div>
                </div>

                <div className="wl-line">
                  <div
                    className="wl-ico"
                    style={{ background: `${Colors.RED}1a`, color: Colors.RED }}
                  >
                    <i className="bi bi-cash-coin" />
                  </div>
                  <div>
                    <p className="wl-line-label">Commission owed</p>
                    <p className="wl-line-hint">
                      Uncollected from operators, including refund shortfalls
                    </p>
                  </div>
                  <div className="wl-line-value">
                    {loading ? (
                      <Skeleton height={20} width={80} />
                    ) : (
                      formatCurrency(cf.cash_commission_owed)
                    )}
                  </div>
                </div>

                <div className="wl-line">
                  <div
                    className="wl-ico"
                    style={{
                      background: `${WALLET_START}1a`,
                      color: WALLET_START,
                    }}
                  >
                    <i className="bi bi-people" />
                  </div>
                  <div>
                    <p className="wl-line-label">Operator balances</p>
                    <p className="wl-line-hint">Owed to active operators</p>
                  </div>
                  <div className="wl-line-value">
                    {loading ? (
                      <Skeleton height={20} width={80} />
                    ) : (
                      formatCurrency(cf.operator_obligations)
                    )}
                  </div>
                </div>

                {hasRecon && (
                  <div className="wl-line">
                    <div
                      className="wl-ico"
                      style={{
                        background: reconOk
                          ? `${Colors.SUCCESS}1a`
                          : `${Colors.WARNING}26`,
                        color: reconOk ? Colors.SUCCESS : WARN_TEXT,
                      }}
                    >
                      <i
                        className={`bi ${reconOk ? "bi-check2-circle" : "bi-exclamation-triangle"}`}
                      />
                    </div>
                    <div>
                      <p className="wl-line-label">Ledger check</p>
                      <p className="wl-line-hint">
                        {reconOk
                          ? "Wallet position reconciles with commission, fees and withheld cash commission"
                          : `Wallet holds ${formatCurrency(cf.platform_share_in_wallet)} for the platform, expected ${formatCurrency(cf.expected_platform_share)}. Check manual payments, inactive operators and test data.`}
                      </p>
                    </div>
                    <div
                      className="wl-line-value"
                      style={{ color: reconOk ? Colors.SUCCESS : WARN_TEXT }}
                    >
                      {reconOk
                        ? "Matches"
                        : `${reconDiff > 0 ? "+" : "-"} ${formatCurrency(Math.abs(reconDiff))}`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* ============ Main column ============ */}
          <Col xs={12} lg={8}>
            <div className="d-flex flex-column gap-4">
              {/* Cash flow */}
              <div className="wl-card wl-card-pad">
                <div className="wl-flow-head">
                  <div>
                    <h5>Cash flow</h5>
                    <small>Payments received and money paid out</small>
                  </div>
                  <div className="wl-seg" role="tablist" aria-label="Period">
                    {PERIODS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        role="tab"
                        aria-selected={period === p.key}
                        className={period === p.key ? "active" : ""}
                        onClick={() => setPeriod(p.key)}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wl-tiles">
                  <div className="wl-tile">
                    <p className="wl-tile-label">
                      <i
                        className="bi bi-arrow-down-left"
                        style={{
                          background: `${Colors.SUCCESS}1a`,
                          color: Colors.SUCCESS,
                        }}
                      />
                      Payments received
                    </p>
                    <p
                      className="wl-tile-value"
                      style={{ color: Colors.SUCCESS }}
                    >
                      {loading ? (
                        <Skeleton height={26} width="60%" />
                      ) : (
                        formatCurrency(flow.in)
                      )}
                    </p>
                    <p className="wl-tile-hint">{inHint}</p>
                  </div>
                  <div className="wl-tile">
                    <p className="wl-tile-label">
                      <i
                        className="bi bi-arrow-up-right"
                        style={{ background: `${ACCENT}1f`, color: ACCENT }}
                      />
                      Paid out
                    </p>
                    <p className="wl-tile-value">
                      {loading ? (
                        <Skeleton height={26} width="60%" />
                      ) : (
                        formatCurrency(flow.out)
                      )}
                    </p>
                    <p className="wl-tile-hint">{outHint}</p>
                  </div>
                </div>

                {dailyFlow.length > 0 && (
                  <div className="wl-chart">
                    <div className="wl-chart-legend">
                      <span>Last 14 days</span>
                      <span>
                        <i style={{ background: WALLET_START }} />
                        Received
                      </span>
                      <span>
                        <i style={{ background: ACCENT }} />
                        Paid out
                      </span>
                    </div>
                    <FlowChart data={dailyFlow} />
                  </div>
                )}
              </div>

              {/* Activity */}
              <div className="wl-card">
                <div className="wl-act-head">
                  <div className="wl-act-top">
                    <div>
                      <h5>Recent activity</h5>
                      <small>
                        {source === "ledger"
                          ? "Latest 25 platform transactions"
                          : "Latest 10 transactions reported by PayMongo"}
                      </small>
                    </div>
                    <div className="wl-seg" role="tablist" aria-label="Source">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={source === "ledger"}
                        className={source === "ledger" ? "active" : ""}
                        onClick={() => setSource("ledger")}
                      >
                        Platform ledger
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={source === "paymongo"}
                        className={source === "paymongo" ? "active" : ""}
                        onClick={() => setSource("paymongo")}
                      >
                        PayMongo wallet
                      </button>
                    </div>
                  </div>

                  <div
                    className="wl-tabs"
                    role="tablist"
                    aria-label="Direction"
                  >
                    {FILTERS.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        role="tab"
                        aria-selected={filter === f.key}
                        className={`wl-tab${filter === f.key ? " active" : ""}`}
                        onClick={() => setFilter(f.key)}
                      >
                        {f.label}
                        <span className="wl-tab-count">{counts[f.key]}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  {loading && rows.length === 0 ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : groups.length === 0 ? (
                    <div className="wl-empty">
                      <i className="bi bi-receipt" />
                      {rows.length === 0
                        ? "No transactions yet. Activity will show up here."
                        : "No transactions match this filter."}
                    </div>
                  ) : (
                    groups.map((g) => (
                      <div key={g.label}>
                        <div className="wl-day">
                          <span>{g.label}</span>
                          <span>
                            {g.items.length}{" "}
                            {g.items.length === 1 ? "item" : "items"}
                          </span>
                        </div>
                        {g.items.map((r) => {
                          const isIn = r.dir === "in";
                          const isOut = r.dir === "out";
                          const meta =
                            TYPE_META[r.type] ||
                            (isOut
                              ? TYPE_META.payout
                              : isIn
                                ? TYPE_META.rental_payment
                                : {
                                    icon: "bi-arrow-left-right",
                                    color: Colors.GRAY,
                                    bg: `${Colors.GRAY}26`,
                                  });
                          const isCash =
                            (r.method || "").toLowerCase() === "cash";
                          const sub = [
                            formatTime(r.at),
                            source === "paymongo" && r.status
                              ? String(r.status)
                              : null,
                            r.type === "rental_payment" && num(r.commission) > 0
                              ? `Commission ${formatCurrency(r.commission)}`
                              : null,
                            r.type === "payout" && num(r.withheld) > 0
                              ? `Gross ${formatCurrency(r.gross)} · ${formatCurrency(r.withheld)} commission withheld`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ");

                          return (
                            <div key={r.key} className="wl-tx">
                              <div
                                className="wl-tx-icon"
                                style={{
                                  background: meta.bg,
                                  color: meta.color,
                                }}
                              >
                                <i className={`bi ${meta.icon}`} />
                              </div>
                              <div className="wl-tx-main">
                                <p className="wl-tx-title">{r.title}</p>
                                <p className="wl-tx-sub">{sub || "—"}</p>
                              </div>
                              {r.method && (
                                <span
                                  className={`wl-pill${isCash ? " cash" : ""}`}
                                  title={
                                    isCash
                                      ? "Cash is held by the operator and never enters the PayMongo wallet"
                                      : undefined
                                  }
                                >
                                  {String(r.method).toUpperCase()}
                                </span>
                              )}
                              <div className="wl-tx-right">
                                <div
                                  className="wl-tx-amount"
                                  style={{
                                    color: isIn
                                      ? Colors.SUCCESS
                                      : Colors.TEXT_DARK,
                                  }}
                                >
                                  {isIn ? "+" : isOut ? "-" : ""}{" "}
                                  {formatCurrency(r.amount)}
                                </div>
                                {num(r.fee) > 0 && (
                                  <div className="wl-tx-fee">
                                    Fee {formatCurrency(r.fee)}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>

                {source === "paymongo" && (
                  <div className="wl-note">
                    {paymongo?.api_note ||
                      "PayMongo returns only the last 10 wallet transactions."}
                    {paymongo?.last_synced
                      ? ` Synced ${formatTime(paymongo.last_synced)}.`
                      : ""}{" "}
                    Processing fees appear here but are not subtracted from the
                    net wallet position.
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Wallet;
